import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { earningsService } from '../../services/earningsService';
import { EarningsTrade, EarningsPosition, EarningsPlay, EarningsDiscoveryDetail, StrategyRecommendation } from '../../types/earnings';
import { fmt, gradeColor } from '../../utils/formatters';

const STRATEGY_LABELS: Record<string, string> = {
  iron_condor: 'Iron Condor',
  bear_call_spread: 'Bear Call Spread',
  bull_put_spread: 'Bull Put Spread',
  long_put_calendar: 'Put Calendar',
  call_calendar: 'Call Calendar',
  put_calendar: 'Put Calendar',
};

const RecommendationCard: React.FC<{
  rec: StrategyRecommendation;
  onSelect?: (rec: StrategyRecommendation) => void;
}> = ({ rec, onSelect }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      opacity: rec.executable ? 1 : 0.5,
      cursor: onSelect && rec.executable ? 'pointer' : 'default',
      '&:hover': onSelect && rec.executable ? { bgcolor: 'action.hover' } : {},
    }}
    onClick={() => onSelect && rec.executable && onSelect(rec)}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
      <Typography variant="subtitle2" fontWeight={700}>
        {STRATEGY_LABELS[rec.strategy] ?? rec.strategy}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {!rec.executable && <Chip label="Not enough data" size="small" color="default" />}
        {rec.size_recommendation && (
          <Chip label={rec.size_recommendation} size="small" variant="outlined" />
        )}
      </Box>
    </Box>
    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
      {Object.entries(rec.strikes).map(([leg, value]) => (
        <Box key={leg}>
          <Typography variant="caption" color="text.secondary">
            {leg === 'far_expiry' ? 'Far Expiry' : leg}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {leg === 'far_expiry' ? fmt.shortDate(String(value)) : fmt.strike(value)}
          </Typography>
        </Box>
      ))}
      <Box>
        <Typography variant="caption" color="text.secondary">Expiry</Typography>
        <Typography variant="body2" fontWeight={600}>{fmt.shortDate(rec.expiry)}</Typography>
      </Box>
      {rec.estimated_credit && (
        <Box>
          <Typography variant="caption" color="text.secondary">Est. Credit</Typography>
          <Typography variant="body2" fontWeight={600} color="success.main">
            {fmt.currency(rec.estimated_credit)}
          </Typography>
        </Box>
      )}
      {rec.credit_pct_width && (
        <Box>
          <Typography variant="caption" color="text.secondary">Credit/Risk</Typography>
          <Typography variant="body2" fontWeight={600}>
            {(Number(rec.credit_pct_width) * 100).toFixed(1)}%
          </Typography>
        </Box>
      )}
    </Box>
  </Paper>
);

const AiColorSection: React.FC<{ text: string }> = ({ text }) => (
  <Card
    variant="outlined"
    sx={{
      borderColor: 'primary.main',
      borderWidth: 1.5,
      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.05)' : 'rgba(33, 150, 243, 0.04)',
    }}
  >
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <AutoAwesomeIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" fontWeight={700} color="primary">
          AI Analysis
        </Typography>
        <Chip label="Claude" size="small" color="primary" variant="outlined" sx={{ ml: 'auto' }} />
      </Box>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
        {text}
      </Typography>
    </CardContent>
  </Card>
);

// Dialog for creating a new earnings trade from discovery
interface CreateTradeDialogProps {
  open: boolean;
  ticker: string;
  earningsDate: string;
  discovery: EarningsDiscoveryDetail;
  initialStrategy?: string;
  onClose: () => void;
  onCreate: (strategyType: string, contracts: number, legs: EditableLeg[], entryPrice?: number, tradeDate?: string) => Promise<void>;
}

const DEBIT_STRATEGIES = new Set(['call_calendar', 'put_calendar', 'long_put_calendar']);

const nowLocalIso = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:mm"
};

interface EditableLeg {
  label: string;
  side: 'buy' | 'sell';
  option_type: 'C' | 'P';
  strike: string;
  expiry: string;
}

const buildLegsFromRec = (rec: StrategyRecommendation | undefined): EditableLeg[] => {
  if (!rec) return [];
  return Object.entries(rec.strikes).map(([legLabel, strike]) => ({
    label: legLabel,
    side: (legLabel.startsWith('short') ? 'sell' : 'buy') as 'buy' | 'sell',
    option_type: (legLabel.includes('call') ? 'C' : 'P') as 'C' | 'P',
    strike: String(strike),
    expiry: rec.expiry,
  }));
};

const CreateTradeDialog: React.FC<CreateTradeDialogProps> = ({
  open, ticker, earningsDate, discovery, initialStrategy, onClose, onCreate,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [strategyType, setStrategyType] = useState(
    initialStrategy ?? discovery.recommendations[0]?.strategy ?? 'iron_condor'
  );
  const [contracts, setContracts] = useState(1);
  const [entryPrice, setEntryPrice] = useState('');
  const [tradeDate, setTradeDate] = useState(nowLocalIso());
  const [method, setMethod] = useState<'execute' | 'log'>('log');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const strategyOptions = discovery.recommendations.length > 0
    ? discovery.recommendations.map((r) => r.strategy)
    : Object.keys(STRATEGY_LABELS);

  const selectedRec = discovery.recommendations.find((r) => r.strategy === strategyType);

  const defaultLeg: EditableLeg = { label: 'leg_1', side: 'sell', option_type: 'P', strike: '', expiry: '' };
  const [legs, setLegs] = useState<EditableLeg[]>(() => {
    const built = buildLegsFromRec(selectedRec);
    return built.length > 0 ? built : [defaultLeg];
  });

  // Re-fill legs and entry price when strategy changes
  React.useEffect(() => {
    const built = buildLegsFromRec(selectedRec);
    setLegs(built.length > 0 ? built : [defaultLeg]);
    if (selectedRec?.estimated_credit) {
      setEntryPrice(Number(selectedRec.estimated_credit).toFixed(2));
    } else {
      setEntryPrice('');
    }
  }, [strategyType]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateLeg = (index: number, field: keyof EditableLeg, value: string) => {
    setLegs((prev) => prev.map((l, i) => i === index ? { ...l, [field]: value } : l));
  };

  const isDebit = DEBIT_STRATEGIES.has(strategyType);
  const priceLabel = isDebit ? 'Debit paid / share' : 'Credit received / share';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (method === 'execute') return;
    setErr('');
    for (const leg of legs) {
      if (!leg.strike || isNaN(Number(leg.strike))) { setErr('Enter a valid strike for each leg'); return; }
      if (!leg.expiry) { setErr('Enter an expiry for each leg'); return; }
    }
    setSaving(true);
    try {
      await onCreate(
        strategyType,
        contracts,
        legs,
        entryPrice ? Number(entryPrice) : undefined,
        tradeDate,
      );
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="sm" fullWidth>
      <DialogTitle>Create Earnings Trade — {ticker}</DialogTitle>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Earnings: {fmt.date(earningsDate)}
            {discovery.grade && (
              <> &nbsp;·&nbsp; Grade: <strong style={{ color: gradeColor(discovery.grade) }}>{discovery.grade}</strong></>
            )}
          </Typography>

          <FormControl size="small" fullWidth>
            <InputLabel>Strategy</InputLabel>
            <Select
              value={strategyType}
              label="Strategy"
              onChange={(e) => setStrategyType(e.target.value)}
            >
              {strategyOptions.map((s) => (
                <MenuItem key={s} value={s}>
                  {STRATEGY_LABELS[s] ?? s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Editable legs */}
          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Legs</Typography>
            {legs.map((leg, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Select size="small" value={leg.side} onChange={(e) => updateLeg(i, 'side', e.target.value)} sx={{ width: 80 }}>
                  <MenuItem value="buy">Buy</MenuItem>
                  <MenuItem value="sell">Sell</MenuItem>
                </Select>
                <Select size="small" value={leg.option_type} onChange={(e) => updateLeg(i, 'option_type', e.target.value)} sx={{ width: 80 }}>
                  <MenuItem value="C">Call</MenuItem>
                  <MenuItem value="P">Put</MenuItem>
                </Select>
                <TextField size="small" label="Strike" type="number" value={leg.strike}
                  onChange={(e) => updateLeg(i, 'strike', e.target.value)}
                  inputProps={{ step: 0.5 }} sx={{ width: 100 }} />
                <TextField size="small" label="Expiry" type="date" value={leg.expiry}
                  onChange={(e) => updateLeg(i, 'expiry', e.target.value)}
                  InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
                {legs.length > 1 && (
                  <Button size="small" color="error" sx={{ minWidth: 0, px: 0.5 }}
                    onClick={() => setLegs((prev) => prev.filter((_, idx) => idx !== i))}>✕</Button>
                )}
              </Box>
            ))}
            <Button size="small" sx={{ textTransform: 'none', mt: 0.5 }}
              onClick={() => setLegs((prev) => [...prev, { label: `leg_${prev.length + 1}`, side: 'sell', option_type: 'P', strike: '', expiry: prev[0]?.expiry || '' }])}>
              + Add leg
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Contracts"
              type="number"
              size="small"
              value={contracts}
              onChange={(e) => setContracts(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1 }}
              sx={{ width: 120 }}
            />
            <TextField
              label={priceLabel}
              type="number"
              size="small"
              value={entryPrice}
              onChange={(e) => setEntryPrice(e.target.value)}
              inputProps={{ step: 0.01 }}
              placeholder={selectedRec?.estimated_credit ? `est. ${Number(selectedRec.estimated_credit).toFixed(2)}` : ''}
              fullWidth
            />
          </Box>

          <TextField
            label="Trade timestamp"
            type="datetime-local"
            size="small"
            value={tradeDate}
            onChange={(e) => setTradeDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          <Divider />

          {/* Execute vs Log */}
          <RadioGroup value={method} onChange={(e) => setMethod(e.target.value as 'execute' | 'log')} row={!fullScreen}>
            <FormControlLabel
              value="execute"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>Execute Trade</Typography>
                  <Typography variant="caption" color="text.secondary">via Tradier API</Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="log"
              control={<Radio size="small" />}
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>Log Trade</Typography>
                  <Typography variant="caption" color="text.secondary">manual entry</Typography>
                </Box>
              }
            />
          </RadioGroup>

          {method === 'execute' && (
            <Alert severity="info">
              Broker API execution is not yet available. Use <strong>Log Trade</strong> to manually record this position.
            </Alert>
          )}

          {err && <Alert severity="error">{err}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={saving || method === 'execute'}>
            {saving ? 'Creating…' : 'Log Trade'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

type CloseReason = 'expired' | 'bought_back' | 'assigned';

interface CloseTradeDialogProps {
  open: boolean;
  group: GroupedTrade;
  onClose: () => void;
  onConfirm: (closeDate: string, closeReason: CloseReason, closePrice?: number, contractsToClose?: number) => Promise<void>;
}

const CloseTradeDialog: React.FC<CloseTradeDialogProps> = ({ open, group, onClose, onConfirm }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const today = new Date().toISOString().split('T')[0];
  const [closeDate, setCloseDate] = useState(today);
  const [reason, setReason] = useState<CloseReason>('expired');
  const [closePrice, setClosePrice] = useState('');
  const [contractsToClose, setContractsToClose] = useState(group.totalContracts);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const needsClosePrice = reason === 'bought_back' || reason === 'assigned';
  const isMulti = group.totalContracts > 1;

  const pnl = (): number | null => {
    if (group.avgEntryPrice == null) return null;
    const credit = group.avgEntryPrice;
    if (reason === 'expired') return credit * contractsToClose * 100;
    if (needsClosePrice && closePrice) return (credit - Number(closePrice)) * contractsToClose * 100;
    return null;
  };

  const estimatedPnl = pnl();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (needsClosePrice && (!closePrice || isNaN(Number(closePrice)))) {
      setErr('Enter the close price per share');
      return;
    }
    setErr('');
    setSaving(true);
    try {
      await onConfirm(
        closeDate,
        reason,
        needsClosePrice && closePrice ? Number(closePrice) : undefined,
        contractsToClose < group.totalContracts ? contractsToClose : undefined,
      );
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="xs" fullWidth>
      <DialogTitle>Close Trade</DialogTitle>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>

          {/* Trade context */}
          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
            <Typography variant="body2" fontWeight={600}>
              {STRATEGY_LABELS[group.strategy_type] ?? group.strategy_type}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {group.positions.length > 0 ? formatStrategyDetails(group.positions) : '—'}
              {group.avgEntryPrice != null && ` · Avg entry: +${fmt.currency(group.avgEntryPrice.toFixed(2))}/share`}
              {` · ${group.totalContracts} contract${group.totalContracts !== 1 ? 's' : ''}`}
              {group.trades.length > 1 && ` (${group.trades.length} fills)`}
            </Typography>
          </Box>

          {/* Contracts to close — only shown when group has more than 1 contract */}
          {isMulti && (
            <TextField
              label="Contracts to close"
              type="number"
              size="small"
              value={contractsToClose}
              onChange={(e) => setContractsToClose(Math.min(group.totalContracts, Math.max(1, parseInt(e.target.value) || 1)))}
              inputProps={{ min: 1, max: group.totalContracts, step: 1 }}
              helperText={`Max ${group.totalContracts}. Oldest fills closed first.`}
              fullWidth
            />
          )}

          {/* Close reason */}
          <RadioGroup value={reason} onChange={(e) => setReason(e.target.value as CloseReason)}>
            <FormControlLabel value="expired" control={<Radio size="small" />} label="Expired worthless (full premium kept)" />
            <FormControlLabel value="bought_back" control={<Radio size="small" />} label="Bought back before expiry" />
            <FormControlLabel value="assigned" control={<Radio size="small" />} label="Assigned / exercised" />
          </RadioGroup>

          {/* Close price — only for bought_back / assigned */}
          {needsClosePrice && (
            <TextField
              label={reason === 'assigned' ? 'Net debit to close / share' : 'Buyback price / share'}
              helperText={reason === 'assigned'
                ? 'Net debit paid to unwind the position'
                : 'Debit paid per share to buy back'}
              type="number"
              size="small"
              value={closePrice}
              onChange={(e) => setClosePrice(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
              fullWidth
            />
          )}

          {/* P&L preview */}
          {estimatedPnl !== null && (
            <Box sx={{ bgcolor: estimatedPnl >= 0 ? 'success.light' : 'error.light', borderRadius: 1, p: 1, opacity: 0.85 }}>
              <Typography variant="body2" fontWeight={700} color={estimatedPnl >= 0 ? 'success.dark' : 'error.dark'}>
                Estimated P&L: {estimatedPnl >= 0 ? '+' : ''}${estimatedPnl.toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {contractsToClose} contract{contractsToClose !== 1 ? 's' : ''} × 100 shares
              </Typography>
            </Box>
          )}

          <TextField
            label="Close date"
            type="date"
            size="small"
            value={closeDate}
            onChange={(e) => setCloseDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          {err && <Alert severity="error">{err}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="contained" color="error" disabled={saving}>
            {saving ? 'Closing…' : 'Close Trade'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

const formatStrategyDetails = (positions: EarningsPosition[]): string => {
  const puts = positions.filter(p => p.option_type === 'P').sort((a, b) => Number(b.strike) - Number(a.strike));
  const calls = positions.filter(p => p.option_type === 'C').sort((a, b) => Number(a.strike) - Number(b.strike));
  const parts: string[] = [];
  if (puts.length >= 2) parts.push(`${Number(puts[0].strike)}/${Number(puts[1].strike)} Puts`);
  else if (puts.length === 1) parts.push(`${Number(puts[0].strike)} Put`);
  if (calls.length >= 2) parts.push(`${Number(calls[0].strike)}/${Number(calls[1].strike)} Calls`);
  else if (calls.length === 1) parts.push(`${Number(calls[0].strike)} Call`);
  return parts.join(' · ') || '—';
};

interface GroupedTrade {
  key: string;
  strategy_type: string;
  positions: EarningsPosition[];   // from first trade (legs are identical across group)
  totalContracts: number;
  avgEntryPrice: number | null;    // weighted average
  totalRealizedPnl: number | null; // sum of realized_pnl for closed trades
  tradeIds: number[];              // ordered oldest→newest
  trades: EarningsTrade[];
  status: 'open' | 'closed';
}

function tradeGroupKey(t: EarningsTrade): string {
  return `${t.status}|${t.strategy_type}|${formatStrategyDetails(t.positions)}|${t.positions[0]?.expiry ?? ''}`;
}

function groupTrades(tradeList: EarningsTrade[]): GroupedTrade[] {
  const map = new Map<string, GroupedTrade>();
  for (const t of tradeList) {
    const key = tradeGroupKey(t);
    const existing = map.get(key);
    if (existing) {
      const prevContracts = existing.totalContracts;
      existing.totalContracts += t.contracts;
      // Weighted average entry price
      if (t.total_credit != null) {
        const prevCredit = (existing.avgEntryPrice ?? 0) * prevContracts;
        existing.avgEntryPrice = (prevCredit + Number(t.total_credit) * t.contracts) / existing.totalContracts;
      }
      // Sum realized P&L for closed trades
      if (t.realized_pnl != null) {
        existing.totalRealizedPnl = (existing.totalRealizedPnl ?? 0) + Number(t.realized_pnl);
      }
      existing.tradeIds.push(t.id);
      existing.trades.push(t);
    } else {
      map.set(key, {
        key,
        strategy_type: t.strategy_type,
        positions: t.positions,
        totalContracts: t.contracts,
        avgEntryPrice: t.total_credit != null ? Number(t.total_credit) : null,
        totalRealizedPnl: t.realized_pnl != null ? Number(t.realized_pnl) : null,
        tradeIds: [t.id],
        trades: [t],
        status: t.status as 'open' | 'closed',
      });
    }
  }
  return Array.from(map.values());
}

// Main component
const EarningsDetail: React.FC = () => {
  const { id, ticker: paramTicker } = useParams<{ id?: string; ticker?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const isDiscovery = !id;
  const ticker = paramTicker?.toUpperCase();
  const earningsDateParam = searchParams.get('date') ?? undefined;
  const playIdParam = searchParams.get('play') ?? undefined;

  const [trade, setTrade] = useState<EarningsTrade | null>(null);
  const [trades, setTrades] = useState<EarningsTrade[]>([]);
  const [discovery, setDiscovery] = useState<EarningsDiscoveryDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [play, setPlay] = useState<EarningsPlay | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState<string | undefined>();
  const [selectedGroup, setSelectedGroup] = useState<GroupedTrade | null>(null);
  const [unrealizedMap, setUnrealizedMap] = useState<Record<number, number | null>>({});
  const loadingUnrealizedRef = useRef(new Set<number>());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isDiscovery && ticker) {
        // Load DB data first (skip_live=true) for instant page render
        const [disc, eventTrades, eventPlays] = await Promise.all([
          earningsService.getDiscoveryDetail(ticker, earningsDateParam, true),
          earningsService.listTradesForEvent(ticker, earningsDateParam ?? ''),
          earningsDateParam ? earningsService.listPlays(ticker, earningsDateParam) : Promise.resolve([]),
        ]);
        setDiscovery(disc);

        // If viewing a specific archived play (via ?play=ID), show only that play's trades
        if (playIdParam) {
          const pid = parseInt(playIdParam);
          const archivedPlay = eventPlays.find((p: any) => p.id === pid) ?? null;
          setPlay(archivedPlay);
          setTrades(eventTrades.filter((t: any) => t.play_id === pid));
        } else {
          // Show open play and its trades (or unlinked trades)
          const openPlay = eventPlays.find((p: any) => p.status === 'open') ?? null;
          setPlay(openPlay);
          setTrades(openPlay
            ? eventTrades.filter((t: any) => t.play_id === openPlay.id)
            : eventTrades.filter((t: any) => !t.play_id || !eventPlays.some((p: any) => p.status === 'archived' && p.id === t.play_id)),
          );
        }

        // Refresh with live prices in background
        earningsService.getDiscoveryDetail(ticker, earningsDateParam, false)
          .then((liveDisc) => setDiscovery(liveDisc))
          .catch(() => {}); // keep DB data on failure
      } else if (id) {
        const t = await earningsService.getTrade(parseInt(id));
        setTrade(t);
        // Best-effort load discovery for context (DB first, then live)
        try {
          const dbDisc = await earningsService.getDiscoveryDetail(t.ticker, t.earnings_date, true);
          setDiscovery(dbDisc);
          earningsService.getDiscoveryDetail(t.ticker, t.earnings_date, false)
            .then((liveDisc) => setDiscovery(liveDisc))
            .catch(() => {});
        } catch {
          // Discovery may not be available for past earnings
        }
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, ticker, isDiscovery, earningsDateParam]);

  useEffect(() => { load(); }, [load]);

  // Load unrealized P&L for all open trades whenever trades list changes
  useEffect(() => {
    const openTrades = trades.filter((t) => t.status === 'open');
    for (const t of openTrades) {
      if (loadingUnrealizedRef.current.has(t.id)) continue;
      loadingUnrealizedRef.current.add(t.id);
      earningsService.getUnrealizedPnl(t.id)
        .then((data) => setUnrealizedMap((prev) => ({ ...prev, [t.id]: data.unrealized_pnl })))
        .catch(() => setUnrealizedMap((prev) => ({ ...prev, [t.id]: null })))
        .finally(() => loadingUnrealizedRef.current.delete(t.id));
    }
  }, [trades]);

  const handleCreateTrade = async (
    strategyType: string,
    contracts: number,
    editableLegs: EditableLeg[],
    entryPrice?: number,
    tradeDate?: string,
  ) => {
    const activeTicker = ticker ?? trade?.ticker;
    if (!activeTicker || !discovery) return;
    const entryDate = tradeDate ? tradeDate.split('T')[0] : new Date().toISOString().split('T')[0];

    const legs = editableLegs
      .filter((l) => l.strike && Number(l.strike) > 0)
      .map((l) => ({
        side: l.side,
        option_type: l.option_type,
        strike: Number(l.strike),
        expiry: l.expiry,
        ticker: activeTicker,
      }));

    const t = await earningsService.createTrade({
      ticker: activeTicker,
      earnings_date: discovery.earnings_date,
      strategy_type: strategyType,
      contracts,
      entry_date: entryDate,
      total_credit: entryPrice ?? null,
      legs,
    } as any);

    setCreateOpen(false);
    if (isDiscovery) {
      load(); // reload trades list, stay on discovery page
    } else {
      navigate(`/portfolio/earnings/${t.id}`);
    }
  };

  const handleCloseTrade = async (closeDate: string, closeReason: string, closePrice?: number, contractsToClose?: number) => {
    const group = selectedGroup ?? (trade ? {
      tradeIds: [trade.id],
      trades: [trade],
      totalContracts: trade.contracts,
    } as GroupedTrade : null);
    if (!group) return;

    // FIFO: close oldest trades first until we've covered contractsToClose
    let remaining = contractsToClose ?? group.totalContracts;
    for (const t of group.trades) {
      if (remaining <= 0) break;
      if (t.status !== 'open') continue;
      await earningsService.closeTrade(t.id, {
        close_date: closeDate,
        close_reason: closeReason,
        close_price: closePrice,
      });
      remaining -= t.contracts;
    }

    setCloseOpen(false);
    setSelectedGroup(null);
    load();
  };

  const handleArchivePlay = async () => {
    const t = ticker ?? trade?.ticker;
    const ed = earningsDateParam ?? discovery?.earnings_date ?? trade?.earnings_date;
    if (!t || !ed) return;
    await earningsService.archivePlay(t, ed);
    navigate('/earnings');
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  const displayTicker = ticker ?? trade?.ticker ?? '';
  const displayEarningsDate = discovery?.earnings_date ?? trade?.earnings_date ?? '';
  const grade = discovery?.grade ?? trade?.grade ?? null;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>Back</Button>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h5" fontWeight={700}>{displayTicker}</Typography>
            {grade && (
              <Chip
                label={grade === 'S' ? 'Skip' : `Grade ${grade}`}
                size="small"
                sx={{ bgcolor: gradeColor(grade), color: 'white', fontWeight: 700 }}
              />
            )}
            {trade && (
              <Chip
                label={trade.status}
                size="small"
                color={trade.status === 'open' ? 'success' : 'default'}
                variant="outlined"
              />
            )}
            {play?.status === 'archived' && (
              <Chip label="Archived" size="small" variant="outlined" />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Earnings: {fmt.date(displayEarningsDate)}
            {discovery?.current_price && ` · Price: ${fmt.currency(discovery.current_price)}`}
            {discovery?.max_pain && ` · Max Pain: ${fmt.currency(discovery.max_pain)}`}
          </Typography>
          {trade && (
            <Typography variant="body2" color="text.secondary">
              Logged: {fmt.date(trade.entry_date)}
            </Typography>
          )}
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {discovery && play?.status !== 'archived' && (
            <Button variant="contained" onClick={() => { setSelectedStrategy(undefined); setCreateOpen(true); }}>
              Create Trade
            </Button>
          )}
          {(isDiscovery ? trades.some((t) => t.status === 'open') : trade?.status === 'open') && (
            <Button
              variant="outlined"
              color="error"
              disabled={selectedGroup === null}
              onClick={() => setCloseOpen(true)}
            >
              Close Trade
            </Button>
          )}
          {/* Archive Play: show when trades exist, all are closed, and play is not already archived */}
          {trades.length > 0 && trades.every((t) => t.status === 'closed') && play?.status !== 'archived' && (
            <Button
              variant="contained"
              color="success"
              onClick={handleArchivePlay}
              sx={{ fontWeight: 700 }}
            >
              Archive Play
            </Button>
          )}
        </Box>
      </Box>

      {/* Play summary — signature visual */}
      {trades.length > 0 && (() => {
        const allTrades = isDiscovery ? trades : trade ? [trade] : [];
        const closedTrades = allTrades.filter((t) => t.status === 'closed');
        const realizedPnl = closedTrades.reduce((sum, t) => sum + (t.realized_pnl != null ? Number(t.realized_pnl) : 0), 0);
        const openTrades = allTrades.filter((t) => t.status === 'open');
        const unrealizedPnl = openTrades.reduce((sum, t) => {
          const u = unrealizedMap[t.id];
          return u != null ? sum + u : sum;
        }, 0);
        const hasUnrealized = openTrades.some((t) => unrealizedMap[t.id] != null);
        const totalPnl = realizedPnl + unrealizedPnl;
        const hasAnyPnl = closedTrades.some((t) => t.realized_pnl != null) || hasUnrealized;

        // Collateral at risk per trade = (spread_width - credit_per_share) × contracts × 100
        const getSpreadWidth = (positions: EarningsPosition[]): number => {
          const calls = positions.filter((p) => p.option_type === 'C').map((p) => Number(p.strike));
          const puts = positions.filter((p) => p.option_type === 'P').map((p) => Number(p.strike));
          const callWidth = calls.length >= 2 ? Math.max(...calls) - Math.min(...calls) : 0;
          const putWidth = puts.length >= 2 ? Math.max(...puts) - Math.min(...puts) : 0;
          return Math.max(callWidth, putWidth);
        };
        const totalCollateral = allTrades.reduce((sum, t) => {
          const width = getSpreadWidth(t.positions);
          const credit = t.total_credit != null ? Number(t.total_credit) : 0;
          if (width <= 0) return sum;
          return sum + (width - credit) * t.contracts * 100;
        }, 0);
        const roi = hasAnyPnl && totalCollateral > 0
          ? (totalPnl / totalCollateral) * 100
          : null;

        // Days in trade: from earliest entry to latest close (or today if still open)
        const entryDates = allTrades.map((t) => new Date(t.entry_date).getTime());
        const earliest = Math.min(...entryDates);
        const latestClose = closedTrades
          .filter((t) => t.close_date)
          .map((t) => new Date(t.close_date!).getTime());
        const hasOpen = allTrades.some((t) => t.status === 'open');
        const endDate = hasOpen ? Date.now() : (latestClose.length > 0 ? Math.max(...latestClose) : Date.now());
        const daysInTrade = Math.max(1, Math.round((endDate - earliest) / (1000 * 60 * 60 * 24)) + 1);

        const pnlColor = totalPnl >= 0 ? 'success.main' : 'error.main';

        return (
          <Paper
            elevation={2}
            sx={{
              p: { xs: 2, sm: 2.5 },
              mb: 3,
              display: 'flex',
              flexWrap: 'wrap',
              gap: { xs: 2, sm: 4 },
              alignItems: 'center',
              justifyContent: 'center',
              borderLeft: 4,
              borderColor: totalPnl >= 0 ? 'success.main' : 'error.main',
            }}
          >
            {/* Total G/L */}
            <Box sx={{ textAlign: 'center', minWidth: 100 }}>
              <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
                Total G/L{hasUnrealized ? ' (live)' : ''}
              </Typography>
              <Typography variant="h5" fontWeight={800} color={hasAnyPnl ? pnlColor : 'text.secondary'}>
                {hasAnyPnl
                  ? `${totalPnl >= 0 ? '+' : ''}${fmt.currency(totalPnl)}`
                  : '—'
                }
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem />

            {/* ROI */}
            <Box sx={{ textAlign: 'center', minWidth: 80 }}>
              <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
                ROI<Typography component="span" variant="caption" sx={{ verticalAlign: 'super', fontSize: '0.6rem', ml: 0.25 }}>*</Typography>
              </Typography>
              <Typography variant="h5" fontWeight={800} color={roi != null ? pnlColor : 'text.secondary'}>
                {roi != null
                  ? `${roi >= 0 ? '+' : ''}${roi.toFixed(1)}%`
                  : '—'
                }
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem />

            {/* APR */}
            {(() => {
              const apr = roi != null ? (roi / daysInTrade) * 365 : null;
              return (
                <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                  <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
                    APR<Typography component="span" variant="caption" sx={{ verticalAlign: 'super', fontSize: '0.6rem', ml: 0.25 }}>*</Typography>
                  </Typography>
                  <Typography variant="h5" fontWeight={800} color={apr != null ? pnlColor : 'text.secondary'}>
                    {apr != null
                      ? `${apr >= 0 ? '+' : ''}${apr.toFixed(1)}%`
                      : '—'
                    }
                  </Typography>
                </Box>
              );
            })()}

            <Divider orientation="vertical" flexItem />

            {/* Days in trade */}
            <Box sx={{ textAlign: 'center', minWidth: 80 }}>
              <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
                Days in Trade
              </Typography>
              <Typography variant="h5" fontWeight={800}>
                {daysInTrade}
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem />

            {/* Trades */}
            <Box sx={{ textAlign: 'center', minWidth: 80 }}>
              <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
                Trades
              </Typography>
              <Typography variant="h5" fontWeight={800}>
                {allTrades.length}
              </Typography>
            </Box>
          </Paper>
        );
      })()}

      {/* Stats row */}
      {(discovery || trade) && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {discovery?.iv_edge_ratio && (
            <Grid item xs={6} sm={3}>
              <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">IV Edge</Typography>
                <Typography fontWeight={700}>{Number(discovery.iv_edge_ratio).toFixed(2)}x</Typography>
              </Paper>
            </Grid>
          )}
          {discovery?.atm_straddle_price && discovery?.current_price && Number(discovery.current_price) > 0 && (
            <Grid item xs={6} sm={3}>
              <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Expected Move</Typography>
                <Typography fontWeight={700}>
                  {fmt.percent((Number(discovery.atm_straddle_price) / Number(discovery.current_price)) * 100)}
                </Typography>
              </Paper>
            </Grid>
          )}
          {discovery?.historical_avg_move && (
            <Grid item xs={6} sm={3}>
              <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Hist. Avg Move</Typography>
                <Typography fontWeight={700}>{fmt.percent(Number(discovery.historical_avg_move) * 100)}</Typography>
              </Paper>
            </Grid>
          )}
          {trade?.total_credit && (
            <Grid item xs={6} sm={3}>
              <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Total Credit</Typography>
                <Typography fontWeight={700} color="success.main">{fmt.currency(trade.total_credit)}</Typography>
              </Paper>
            </Grid>
          )}
          {trade?.realized_pnl != null && (
            <Grid item xs={6} sm={3}>
              <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Realized P&amp;L</Typography>
                <Typography
                  fontWeight={700}
                  color={Number(trade.realized_pnl) >= 0 ? 'success.main' : 'error.main'}
                >
                  {fmt.currency(trade.realized_pnl)}
                </Typography>
              </Paper>
            </Grid>
          )}
          {discovery?.pc_ratio_oi && (
            <Grid item xs={6} sm={3}>
              <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">P/C Ratio (OI)</Typography>
                <Typography fontWeight={700}>{Number(discovery.pc_ratio_oi).toFixed(2)}</Typography>
                <Typography variant="caption" color="text.secondary">positioning</Typography>
              </Paper>
            </Grid>
          )}
          {discovery?.pc_ratio_volume && (
            <Grid item xs={6} sm={3}>
              <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">P/C Ratio (Vol)</Typography>
                <Typography fontWeight={700}>{Number(discovery.pc_ratio_volume).toFixed(2)}</Typography>
                <Typography variant="caption" color="text.secondary">flow</Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}

      {/* GEX Dealer Positioning */}
      {discovery?.gex_signals?.available && (() => {
        const gex = discovery.gex_signals;
        const netGex = gex.net_gex_normalized;
        const regime = netGex > 0.5 ? 'Strong suppression' : netGex > 0 ? 'Mild suppression' : netGex > -0.5 ? 'Mild amplification' : 'Strong amplification';
        const regimeColor = netGex > 0.5 ? 'success.main' : netGex > 0 ? 'success.light' : netGex > -0.5 ? 'warning.main' : 'error.main';
        const convergenceLabel = gex.convergence_pct != null
          ? gex.convergence_pct <= 0.02 ? 'Converged' : gex.convergence_pct >= 0.05 ? 'Divergent' : 'Moderate'
          : null;
        const convergenceColor = convergenceLabel === 'Converged' ? 'success.main' : convergenceLabel === 'Divergent' ? 'error.main' : 'text.secondary';
        const termLabel = gex.term_structure?.term_signal === 'vol_settling' ? 'Vol settling post-earnings'
          : gex.term_structure?.term_signal === 'persistent_risk' ? 'Persistent risk across terms' : 'Neutral';
        const termColor = gex.term_structure?.term_signal === 'vol_settling' ? 'success.main'
          : gex.term_structure?.term_signal === 'persistent_risk' ? 'error.main' : 'text.secondary';
        const changeLabel = gex.change?.direction === 'strengthening' ? 'Strengthening'
          : gex.change?.direction === 'weakening' ? 'Weakening' : 'Stable';
        const changeColor = gex.change?.direction === 'strengthening' ? 'success.main'
          : gex.change?.direction === 'weakening' ? 'warning.main' : 'text.secondary';

        return (
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              Dealer Gamma Positioning (GEX)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Net GEX Regime</Typography>
                <Typography variant="body2" fontWeight={700} color={regimeColor}>{regime}</Typography>
                <Typography variant="caption" color="text.secondary">{netGex.toFixed(3)}</Typography>
              </Grid>
              {gex.gamma_flip != null && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Gamma Flip</Typography>
                  <Typography variant="body2" fontWeight={700}>{fmt.currency(gex.gamma_flip)}</Typography>
                </Grid>
              )}
              {gex.top_call_wall != null && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Call Wall (Resistance)</Typography>
                  <Typography variant="body2" fontWeight={700}>{fmt.currency(gex.top_call_wall)}</Typography>
                </Grid>
              )}
              {gex.top_put_wall != null && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Put Wall (Support)</Typography>
                  <Typography variant="body2" fontWeight={700}>{fmt.currency(gex.top_put_wall)}</Typography>
                </Grid>
              )}
              {convergenceLabel && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Flip / Max Pain</Typography>
                  <Typography variant="body2" fontWeight={700} color={convergenceColor}>
                    {convergenceLabel} ({(gex.convergence_pct! * 100).toFixed(1)}%)
                  </Typography>
                </Grid>
              )}
              {gex.term_structure && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Term Structure</Typography>
                  <Typography variant="body2" fontWeight={700} color={termColor}>{termLabel}</Typography>
                </Grid>
              )}
              {gex.change && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Daily GEX Change</Typography>
                  <Typography variant="body2" fontWeight={700} color={changeColor}>{changeLabel}</Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        );
      })()}

      {/* Grade composition footnote */}
      {discovery?.grade_factors && (
        <Box sx={{ mb: 2, px: 0.5 }}>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.6 }}>
            <strong>Grade {discovery.grade_factors.final_grade}</strong>
            {' — IV edge: '}
            {discovery.grade_factors.iv_edge != null ? `${discovery.grade_factors.iv_edge.toFixed(2)}x` : 'n/a'}
            {` (${discovery.grade_factors.iv_grade})`}
            {' · Best credit/risk: '}
            {`${(discovery.grade_factors.best_credit_pct_width * 100).toFixed(1)}%`}
            {discovery.grade_factors.credit_adjustment !== 'none' && (
              <> · <em>{discovery.grade_factors.credit_adjustment}</em></>
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
            IV edge = post-earnings ATM IV ÷ 12-week avg ATM IV. Credit/risk = credit ÷ capital at risk.
            {' '}&lt;5% forces S, &lt;10% caps at C, ≥20% + IV edge ≥2.0x = A+, ≥15% boosts one letter.
          </Typography>
        </Box>
      )}

      {/* Strategy recommendations */}
      {discovery?.recommendations && discovery.recommendations.length > 0 && (
        <Box mb={3}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Recommended Strategies
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {discovery.recommendations.map((rec, i) => (
              <RecommendationCard
                key={i}
                rec={rec}
                onSelect={isDiscovery ? (r) => {
                  setSelectedStrategy(r.strategy);
                  setCreateOpen(true);
                } : undefined}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Open Trades */}
      {(() => {
        const allTrades = isDiscovery ? trades : trade ? [trade] : [];
        const openGroups = groupTrades(allTrades.filter((t) => t.status === 'open'));
        const closedGroups = groupTrades(allTrades.filter((t) => t.status === 'closed'));

        return (
          <>
            {openGroups.length > 0 && (
              <Box mb={3}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Open Trades ({openGroups.reduce((s, g) => s + g.totalContracts, 0)} contracts)
                </Typography>
                <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                        <TableCell padding="checkbox" />
                        <TableCell>Strategy</TableCell>
                        <TableCell>Details</TableCell>
                        <TableCell>Expiry</TableCell>
                        <TableCell align="right">Contracts</TableCell>
                        <TableCell align="right">Avg Entry</TableCell>
                        <TableCell align="right">Unrealized G/L</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {openGroups.map((g) => {
                        const unrealized = g.tradeIds.reduce<number | null>((sum, id) => {
                          const v = unrealizedMap[id];
                          if (v == null) return sum;
                          return (sum ?? 0) + v;
                        }, null);
                        return (
                          <TableRow key={g.key} selected={selectedGroup?.key === g.key}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                size="small"
                                checked={selectedGroup?.key === g.key}
                                onChange={(e) => setSelectedGroup(e.target.checked ? g : null)}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {STRATEGY_LABELS[g.strategy_type] ?? g.strategy_type}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {g.positions.length > 0 ? formatStrategyDetails(g.positions) : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {g.positions[0]?.expiry ? fmt.shortDate(g.positions[0].expiry) : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">{g.totalContracts}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              {g.avgEntryPrice != null ? (
                                <Typography variant="body2" fontWeight={600} color="success.main">
                                  +{fmt.currency(g.avgEntryPrice.toFixed(2))}/share
                                  {g.trades.length > 1 && (
                                    <Typography component="span" variant="caption" color="text.secondary"> avg</Typography>
                                  )}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {unrealized != null ? (
                                <Typography variant="body2" fontWeight={600} color={unrealized >= 0 ? 'success.main' : 'error.main'}>
                                  {unrealized >= 0 ? '+' : ''}${unrealized.toFixed(2)}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {closedGroups.length > 0 && (
              <Box mb={3}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Closed Trades
                </Typography>
                <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                        <TableCell>Strategy</TableCell>
                        <TableCell>Details</TableCell>
                        <TableCell>Expiry</TableCell>
                        <TableCell align="right">Contracts</TableCell>
                        <TableCell align="right">Avg Entry</TableCell>
                        <TableCell align="right">Close Date</TableCell>
                        <TableCell align="right">Realized G/L</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {closedGroups.map((g) => {
                        const closeDate = g.trades.reduce<string | null>((latest, t) => {
                          if (!t.close_date) return latest;
                          return !latest || t.close_date > latest ? t.close_date : latest;
                        }, null);
                        return (
                          <TableRow key={g.key}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {STRATEGY_LABELS[g.strategy_type] ?? g.strategy_type}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {g.positions.length > 0 ? formatStrategyDetails(g.positions) : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {g.positions[0]?.expiry ? fmt.shortDate(g.positions[0].expiry) : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">{g.totalContracts}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              {g.avgEntryPrice != null ? (
                                <Typography variant="body2" fontWeight={600}>
                                  +{fmt.currency(g.avgEntryPrice.toFixed(2))}/share
                                  {g.trades.length > 1 && (
                                    <Typography component="span" variant="caption" color="text.secondary"> avg</Typography>
                                  )}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="text.secondary">
                                {closeDate ? fmt.shortDate(closeDate) : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {g.totalRealizedPnl != null ? (
                                <Typography variant="body2" fontWeight={700} color={g.totalRealizedPnl >= 0 ? 'success.main' : 'error.main'}>
                                  {g.totalRealizedPnl >= 0 ? '+' : ''}${g.totalRealizedPnl.toFixed(2)}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        );
      })()}

      {/* AI color section */}
      {discovery?.ai_color && (
        <Box mb={3}>
          <AiColorSection text={discovery.ai_color} />
        </Box>
      )}

      {/* ROI / APR footnotes */}
      {trades.length > 0 && (
        <Box sx={{ mt: 2, mb: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            *<strong>ROI</strong> = Total G/L ÷ Capital at Risk.
            Capital at risk is (spread width − credit received) × contracts × 100 — the maximum amount you could lose on the position.
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            *<strong>APR</strong> = (ROI ÷ days held) × 365.
            Annualizes the ROI over the holding period. Assumes the same return rate repeats for a full year, which overstates expected returns on short-duration trades.
          </Typography>
        </Box>
      )}

      {/* Create trade dialog */}
      {createOpen && discovery && (
        <CreateTradeDialog
          open={createOpen}
          ticker={displayTicker}
          earningsDate={discovery.earnings_date}
          discovery={discovery}
          initialStrategy={selectedStrategy}
          onClose={() => setCreateOpen(false)}
          onCreate={handleCreateTrade}
        />
      )}

      {/* Close trade dialog */}
      {closeOpen && selectedGroup && (
        <CloseTradeDialog
          open={closeOpen}
          group={selectedGroup}
          onClose={() => setCloseOpen(false)}
          onConfirm={handleCloseTrade}
        />
      )}
    </Box>
  );
};

export default EarningsDetail;
